/** Endpointing (VOICE-VAD, SDD §20.4).
 *
 *  Deciding when somebody has stopped talking. Push-to-talk answers it by releasing the button, but hands-free needs
 *  the machine to notice, and getting it wrong is the difference between an assistant that interrupts you mid-sentence
 *  and one that sits there after you have finished.
 *
 *  Pure, like `machine.ts`, and for the same reason: the caller supplies levels and elapsed time, so every awkward case
 *  — a pause for breath, a cough, a room that is never quite silent — is a unit test rather than something you find out
 *  about by talking to it.
 *
 *  **Two thresholds, not one.** A single threshold chatters at the boundary: a level sitting exactly on it flips
 *  speech/silence every frame and endpoints in the middle of a word. Speech has to get *over* the upper one to start,
 *  and *under* the lower one to count as silence, so the noise floor has somewhere to sit.
 *
 *  **Speech must last before it counts.** A chair creak clears any threshold for one frame. `minSpeechMs` is what
 *  makes the difference between a word and a noise.
 *
 *  **Silence before speech is different from silence after it.** Before, it means nobody said anything and the turn
 *  should end quietly (§20.4's 8 seconds). After, it means the sentence is over and there is something to send.
 */

export interface VadConfig {
  /** RMS, 0..1. Above this for `minSpeechMs` starts an utterance. */
  readonly speechThreshold: number;
  /** Below this counts as silence. Lower than `speechThreshold` on purpose — see above. */
  readonly silenceThreshold: number;
  readonly minSpeechMs: number;
  /** Trailing silence that ends an utterance. Long enough for a breath, short enough not to feel dead. */
  readonly endpointMs: number;
  /** Silence with nothing said yet — §20.4's 8 seconds. */
  readonly maxSilenceMs: number;
}

export const DEFAULT_VAD: VadConfig = {
  speechThreshold: 0.055,
  silenceThreshold: 0.03,
  minSpeechMs: 120,
  // 700ms: a comma-length pause survives it, an end-of-sentence one does not. Shorter cuts people off mid-thought;
  // much longer and the assistant feels like it is waiting to be sure you are finished, which is its own kind of rude.
  endpointMs: 700,
  maxSilenceMs: 8_000,
};

export interface VadState {
  /** True once an utterance has actually begun — the flag that makes trailing silence mean something. */
  readonly speaking: boolean;
  readonly heard: boolean;
  readonly speechMs: number;
  readonly silenceMs: number;
  /** Set once, on the frame the decision is made. The caller acts and resets. */
  readonly emit: 'speech.start' | 'endpoint' | 'timeout' | null;
}

export const initialVad = (): VadState => ({
  speaking: false,
  heard: false,
  speechMs: 0,
  silenceMs: 0,
  emit: null,
});

/** One frame. `level` is RMS in 0..1; `dtMs` is how long since the last one. */
export function vadStep(
  state: VadState,
  level: number,
  dtMs: number,
  config: VadConfig = DEFAULT_VAD,
): VadState {
  const loud = level >= config.speechThreshold;
  const quiet = level < config.silenceThreshold;

  if (loud) {
    const speechMs = state.speechMs + dtMs;
    // Speech resets the silence counter rather than decrementing it: a word in the middle of a pause means the
    // sentence is not over, and a half-elapsed endpoint timer would end it early.
    const speaking = state.speaking || speechMs >= config.minSpeechMs;
    return {
      speaking,
      heard: state.heard || speaking,
      speechMs,
      silenceMs: 0,
      emit: speaking && !state.speaking ? 'speech.start' : null,
    };
  }

  if (!quiet) {
    // Between the two thresholds. Neither speech nor silence — hold everything where it is, which is the entire
    // point of having two of them.
    return { ...state, emit: null };
  }

  const silenceMs = state.silenceMs + dtMs;
  // A burst that never reached `minSpeechMs` is discarded rather than accumulated: two unrelated creaks a second
  // apart are not a word.
  const speechMs = state.speaking ? state.speechMs : 0;

  if (state.speaking && silenceMs >= config.endpointMs)
    return { speaking: false, heard: true, speechMs, silenceMs, emit: 'endpoint' };

  if (!state.heard && silenceMs >= config.maxSilenceMs)
    return { ...state, speechMs, silenceMs, emit: 'timeout' };

  return { ...state, speechMs, silenceMs, emit: null };
}
